/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * This file contains authentication parameters. Contents of this file
 * is roughly the same across other MSAL.js libraries. These parameters
 * are used to initialize Angular and MSAL Angular configurations in
 * in app.module.ts file.
 */

import {
	MsalGuardConfiguration,
	MsalInterceptorConfiguration,
	ProtectedResourceScopes
} from '@azure/msal-angular';
import {
	LogLevel,
	Configuration,
	BrowserCacheLocation,
	InteractionType,
	IPublicClientApplication,
	PublicClientApplication
} from '@azure/msal-browser';
import { IdTokenClaims } from '@azure/msal-common';
import { environment } from 'src/environments/environment';
import {
	getImportacionApiUrl,
	getImportacionRegistroUrls
} from 'src/app/core/config/importacion.config';

const isIE =
	window.navigator.userAgent.indexOf('MSIE ') > -1 ||
	window.navigator.userAgent.indexOf('Trident/') > -1;

/**
 * Enter here the user flows and custom policies for your B2C application,
 * To learn more about user flows, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
	names: {
		signUpSignIn: environment.signUpSignIn,
		resetPassword: environment.resetPassword
	},
	authorities: {
		signUpSignIn: {
			authority: environment.authority
		},
		resetPassword: {
			authority: environment.authority
		}
	},
	authorityDomain: environment.authorityDomain
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
	auth: {
		clientId: environment.clientId, // This is the ONLY mandatory field that you need to supply.
		authority: b2cPolicies.authorities.signUpSignIn.authority, // Defaults to "https://login.microsoftonline.com/common"
		knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
		redirectUri: environment.redirectUri, // * Estimo sería un setting a nivel de cada environment // Points to window.location.origin by default. You must register this URI on Azure portal/App Registration.
		postLogoutRedirectUri: '/', // * Estimo sería un setting a nivel de cada environment // Points to window.location.origin by default.
		navigateToLoginRequestUrl: false
	},
	cache: {
		cacheLocation: BrowserCacheLocation.SessionStorage, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
		storeAuthStateInCookie: false // Set this to "true" if you are having issues on IE11 or Edge. Remove this line to use Angular Universal
	},
	system: {
		/**
		 * Below you can configure MSAL.js logs. For more information, visit:
		 * https://docs.microsoft.com/azure/active-directory/develop/msal-logging-js
		 */
		tokenRenewalOffsetSeconds: Number(environment.tokenRenewalOffsetSeconds),
		loggerOptions: {
			loggerCallback(logLevel: LogLevel, message: string) {},
			logLevel: LogLevel.Error,
			piiLoggingEnabled: false
		}
	}
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
	apiTodoList: {
		endpoint: 'https://localhost:44351/api/todolist',
		scopes: {
			read: ['https://fabrikamb2c.onmicrosoft.com/TodoList/ToDoList.Read'],
			write: ['https://fabrikamb2c.onmicrosoft.com/TodoList/ToDoList.ReadWrite']
		}
	}
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
	scopes: ['openid', environment.oAuth2Scopes],
	extraQueryParameters: { ui_locales: 'es' }
};

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export const MSALGuardConfigFactory = (): MsalGuardConfiguration => {
	return {
		interactionType: InteractionType.Redirect,
		authRequest: loginRequest
	};
};

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export const MSALInstanceFactory = (): IPublicClientApplication => {
	return new PublicClientApplication(msalConfig);
};

/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
 */
export const MSALInterceptorConfigFactory =
	(): MsalInterceptorConfiguration => {
		const protectedResourceMap = new Map<
			string,
			Array<string | ProtectedResourceScopes> | null
		>([
			[
				`${environment.apiUrl}/RegistroProductor/vincular`,
				[environment.oAuth2Scopes]
			],
			[`${environment.apiUrl}/RegistroProductor`, null],
			[environment.apiUrl, [environment.oAuth2Scopes]],
			[environment.apiUrlTrazabilidad, [environment.oAuth2Scopes]],
			[environment.apiUrlFuentesInformacion, [environment.oAuth2Scopes]]
		]);
		const importacionUrl = getImportacionApiUrl();
		if (importacionUrl) {
			protectedResourceMap.set(`${importacionUrl}/importacion/*`, [
				environment.oAuth2Scopes
			]);
			for (const url of getImportacionRegistroUrls()) {
				protectedResourceMap.set(url, [environment.oAuth2Scopes]);
			}
		}

		return {
			interactionType: InteractionType.Popup,
			protectedResourceMap
		};
	};

export type IdTokenClaimsWithPolicyId = IdTokenClaims & {
	acr?: string;
	tfp?: string;
};
